/**
 * Убирает `token` из адресной строки ДО инициализации аналитики.
 *
 * Зачем: gtag.js при загрузке сам читает `location.href` и отправляет его в
 * `page_location` первого page_view. Если пользователь пришёл по ссылке с
 * `?token=<JWT>` (автологин из писем и соцсетей), JWT утекает в аналитику.
 * Отключение `send_page_view` не помогает — скрипт всё равно снимает URL при
 * старте, поэтому единственный надёжный способ — почистить адрес раньше него.
 *
 * Токен не теряется: возвращаем его вызывающему коду, а адресную строку чистим
 * здесь же. Применяет токен `router/middleware.ts` — там же, где и раньше,
 * чтобы не дублировать логику logout прошлого пользователя.
 *
 * Вызывать ДО `createWebHistory()` (сейчас — в `router/config.ts`): history
 * снимает `location.href` при создании и на первой навигации переписывает
 * адресную строку из этого снимка. Очистка после создания history возвращала
 * `?token` на маршрутах без `allowUrlToken`, например на `/home` (MOB-508).
 */
import Logger from '@web/common/Logger'

const TOKEN_PARAM = 'token'

/**
 * Токен, снятый с URL до старта роутера. Роутер уже не увидит его в query,
 * поэтому передаём значение через модуль — читает `router/middleware.ts`.
 */
let pendingUrlToken: string | null = null

/** Возвращает снятый с URL токен и забывает его (одноразовое чтение). */
export function consumePendingUrlToken (): string | null {
  const token = pendingUrlToken
  pendingUrlToken = null
  return token
}

/**
 * Вырезает `token` из текущего URL.
 *
 * @returns значение токена, если он был в адресе, иначе `null`.
 */
export function stripUrlToken (): string | null {
  // window может отсутствовать в SSR/тестовом окружении.
  if (typeof window === 'undefined' || !window.location) return null

  try {
    const url = new URL(window.location.href)
    const urlToken = url.searchParams.get(TOKEN_PARAM)
    if (!urlToken) return null

    url.searchParams.delete(TOKEN_PARAM)
    // replaceState не перезагружает страницу и не добавляет запись в историю,
    // поэтому кнопка «Назад» не вернёт пользователя на URL с токеном.
    window.history.replaceState(window.history.state, '', url.toString())

    pendingUrlToken = urlToken
    return urlToken
  } catch (error) {
    // Аналитика не должна ломать загрузку приложения из-за кривого URL.
    Logger.error('stripUrlToken failed', error)
    return null
  }
}

export default stripUrlToken
